import {
  DefaultFilterConfig,
  DefaultFilterMode,
} from '@domain/configuration/types';
import { OrderGuideItemsContext } from '@/contexts/order-guide-items-context';
import { SearchFilterResponse } from '@/types';
import { useCallback, useContext } from 'react';

const useResolveCurrentValue = () => {
  const orderGuideContext = useContext(OrderGuideItemsContext);

  return useCallback(
    (field: string): string[] | undefined => {
      switch (field) {
        case 'order_guide':
          return orderGuideContext?.selectedCollection?.uuid
            ? [orderGuideContext.selectedCollection.uuid]
            : undefined;
        default:
          return undefined;
      }
    },
    [orderGuideContext?.selectedCollection?.uuid],
  );
};

export const useApplyDefaultFilters = () => {
  const resolveCurrentValue = useResolveCurrentValue();

  return useCallback(
    (
      defaultFilterConfigs: DefaultFilterConfig[],
      availableFilters: SearchFilterResponse[],
    ): SearchFilterResponse[] => {
      const filtersByField = new Map(
        availableFilters.map((f) => [f.name.original_value, f]),
      );

      const selected: SearchFilterResponse[] = [];
      const seen = new Set<string>();
      for (const config of defaultFilterConfigs) {
        if (seen.has(config.field)) {
          continue;
        }
        seen.add(config.field);
        const filter = filtersByField.get(config.field);
        if (!filter?.values?.length) {
          continue;
        }

        const mode = config.mode ?? DefaultFilterMode.ALL;
        let allowedValues: Set<string> | undefined;

        if (mode === DefaultFilterMode.ALL) {
          selected.push(filter);
          continue;
        } else if (mode === DefaultFilterMode.CURRENT) {
          const currentValues = resolveCurrentValue(config.field);
          if (currentValues?.length) {
            allowedValues = new Set(currentValues);
          }
        } else if (mode === DefaultFilterMode.VALUES && config.values?.length) {
          allowedValues = new Set(config.values);
        }

        if (allowedValues) {
          const values = filter.values.filter((v) =>
            allowedValues.has(v.original_value),
          );
          if (values.length) {
            selected.push({ ...filter, values });
          }
        }
      }
      return selected;
    },
    [resolveCurrentValue],
  );
};
